import { haversineKm } from './plan-partitioning';
import { LatLon, Shift } from './route-optimization.interface';
import { RouteTimeline, StopTimeline } from './route-timeline';
import { addMinutesToClock, clockToMinutes } from './route-times';

/**
 * Cuánto ocuparía el día una secuencia que **nadie calculó**.
 *
 * Existe porque el gestor contesta en vivo el techo de CARGA —la insignia se pone
 * en rojo apenas se suelta un pedido— y no el de TIEMPO. Una ruta editada no tiene
 * horas hasta que se la recalcula contra el motor, y eso deja sin respuesta la
 * pregunta que se hace **mientras se arrastra**: *¿me paso del día?*.
 *
 * **Es una estimación geométrica y se declara como tal.** No conoce calles, ni
 * ventanas horarias, ni recargas. Lo que la hace utilizable es que no inventa
 * constantes: el costo de un kilómetro sale de la PROPIA corrida —los minutos que
 * el motor calculó contra los kilómetros en línea recta de esas mismas rutas—, así
 * que absorbe el rodeo de las calles y la velocidad de ese día sin separarlos.
 *
 * Nunca convive con un tiempo real: el llamador la muestra solo donde no hay nada
 * que afirmar. Dos números para la misma pregunta obligarían a elegir a cuál
 * creerle.
 *
 * Módulo puro: entran ids, coordenadas y minutos; salen kilómetros y una
 * estimación. Sin HTTP, sin Angular.
 */

/** Un tramo ya resuelto que sirve para calibrar. */
export interface CalibrationSample {
  straightKm: number;
  /** Minutos de VIAJE del motor. Sin la atención: el coeficiente solo explica viaje. */
  travelMin: number;
}

/**
 * Los dos costos por kilómetro de una corrida, uno por **régimen**.
 *
 * Un solo coeficiente no alcanza, y el motivo no es de precisión sino de escala. Una
 * ruta mezcla dos cosas que no se parecen:
 *
 * ```
 *   almacén ─── 120 km de carretera ───▶ territorio ── s1→s2→s3 ──
 *              ~1 min/km (60 km/h)                   1,5 km cada uno
 *                                                    ~5 min/km
 * ```
 *
 * Calibrar sobre el TOTAL de la ruta da un número dominado por la carretera —aporta
 * el 92% de los kilómetros— y aplicarlo a un tramo urbano lo deja **4× corto**:
 * `1,5 km × 1,2 = 1,8 min` donde el real son 7. El total de la ruta queda bien
 * (es la escala a la que se midió) y el reparto entre paradas, inservible.
 *
 * Cada coeficiente se mide en la escala **en la que se va a aplicar**, que es lo que
 * el dato del motor permite: el plan informa el tramo de cada parada.
 */
export interface RunCalibration {
  /** Tramos entre paradas. `null` si la corrida no dio ninguno utilizable. */
  urbanMinPerKm: number | null;
  /** Ida del almacén a la primera entrega. Se aplica también al regreso. */
  haulMinPerKm: number | null;
}

/** Las muestras de UNA ruta, separadas por régimen. */
export interface RouteCalibrationSamples {
  haul: CalibrationSample[];
  urban: CalibrationSample[];
}

/** Lo mínimo de una parada del plan para poder calibrar con ella. */
export interface CalibrationStop {
  lat: number | null;
  lon: number | null;
  /** Tramo desde la parada anterior. `0` en la primera, por contrato del motor. */
  legDurationMin?: number;
}

/**
 * Las muestras de calibración de una ruta ya resuelta, separadas por régimen.
 *
 * **La ida se toma de `outboundMin` y el regreso NO se usa.** El regreso del plan se
 * deriva por diferencia (viaje total − suma de tramos), así que arrastra el error de
 * todo lo demás; la ida sale del `etaMin` de la primera parada, que es un dato del
 * motor. Un dato limpio y una suposición declarada —el regreso comparte régimen con
 * la ida— es mejor que dos datos donde uno es residuo del otro.
 */
export function routeCalibrationSamples(
  stops: readonly CalibrationStop[] | undefined,
  warehouse: LatLon | undefined,
  outboundMin: number,
): RouteCalibrationSamples {
  const out: RouteCalibrationSamples = { haul: [], urban: [] };
  const located = (stops ?? []).filter(
    (s): s is CalibrationStop & { lat: number; lon: number } => s?.lat !== null && s?.lon !== null,
  );
  if (!located.length) return out;

  if (warehouse && outboundMin > 0) {
    out.haul.push({
      straightKm: haversineKm([warehouse.lat, warehouse.lon], [located[0].lat, located[0].lon]),
      travelMin: outboundMin,
    });
  }
  for (let i = 1; i < located.length; i++) {
    const legMin = located[i].legDurationMin ?? 0;
    if (!(legMin > 0)) continue;
    out.urban.push({
      straightKm: haversineKm(
        [located[i - 1].lat, located[i - 1].lon],
        [located[i].lat, located[i].lon],
      ),
      travelMin: legMin,
    });
  }
  return out;
}

/** La jornada estimada de una ruta, desglosada. */
export interface EstimatedDay {
  /** Estimado con el coeficiente de la corrida. */
  travelMin: number;
  /** Supuesto de la corrida: paradas × atención. No se estima, se sabe. */
  serviceMin: number;
  totalMin: number;
}

/** `[lat, lon]` de una parada, resuelto por el llamador. */
export type StopCoordOf = (orderId: string) => [number, number] | undefined;

/**
 * Lo que costó un kilómetro en línea recta **en esta corrida**, en minutos.
 *
 * Es el cociente de los TOTALES y no el promedio de los cocientes: promediar le
 * daría el mismo peso a una ruta de 2 km que a una de 200. Es el mismo criterio con
 * el que `runAverages` calcula la velocidad de la flota.
 *
 * Un único coeficiente en vez de una velocidad por un factor de rodeo: dos
 * estimaciones multiplicadas acumulan sus errores, y ninguna de las dos es
 * observable por separado en la respuesta del motor. El cociente sí lo es.
 *
 * `null` sin muestras utilizables. Un número sin respaldo es peor que su ausencia,
 * y el llamador ya sabe no mostrar nada.
 */
export function calibrateMinPerKm(
  samples: readonly CalibrationSample[] | undefined,
): number | null {
  let totalKm = 0;
  let totalMin = 0;
  for (const sample of samples ?? []) {
    // Una muestra sin kilómetros no aporta nada y sus minutos inflarían el
    // coeficiente: se descarta entera, no solo su denominador.
    if (!(sample?.straightKm > 0) || !(sample?.travelMin > 0)) continue;
    totalKm += sample.straightKm;
    totalMin += sample.travelMin;
  }
  if (!(totalKm > 0) || !(totalMin > 0)) return null;
  const coefficient = totalMin / totalKm;
  return Number.isFinite(coefficient) ? coefficient : null;
}

/** El recorrido estimado de una ruta, y su jornada. Salen del MISMO cálculo. */
export interface EstimatedTimeline {
  timeline: RouteTimeline;
  day: EstimatedDay;
}

/**
 * El recorrido estimado, parada por parada.
 *
 * Devuelve el timeline **y** el total juntos a propósito: calcularlos por separado
 * —`km × coeficiente` para la cabecera, suma de tramos redondeados para la columna—
 * daría dos números para la misma pregunta, distintos por unos minutos de redondeo.
 * Una función, un resultado.
 *
 * Sigue la MISMA convención del motor: el primer tramo va en `0` y la ida vive en
 * `etaMin`. Con eso `outboundLegMin`, `returnLegMin` y `stopTimeCost` funcionan sin
 * una rama para lo estimado.
 *
 * Las paradas sin coordenada no reciben tiempos y **no cortan la cadena**: el tramo
 * se mide contra la última parada medible, que es lo que el camión va a recorrer.
 */
export function estimateRouteTimeline(input: {
  orderIds: readonly string[];
  coordOf: StopCoordOf;
  warehouse: LatLon | undefined;
  calibration: RunCalibration;
  serviceMinPerStop: number;
  shiftStart: string;
}): EstimatedTimeline | null {
  const { orderIds, coordOf, warehouse, calibration, serviceMinPerStop, shiftStart } = input;
  // Con uno solo, ese se usa para todo: media calibración estima peor que la
  // completa, pero mucho mejor que no estimar.
  const urban = calibration?.urbanMinPerKm ?? calibration?.haulMinPerKm ?? null;
  const haul = calibration?.haulMinPerKm ?? calibration?.urbanMinPerKm ?? null;
  if (urban === null || haul === null || !warehouse) return null;

  const measurable: Array<{ orderId: string; coord: [number, number] }> = [];
  for (const orderId of orderIds ?? []) {
    const coord = coordOf(orderId);
    if (coord) measurable.push({ orderId, coord });
  }
  if (!measurable.length) return null;

  const home: [number, number] = [warehouse.lat, warehouse.lon];
  const stops = new Map<string, StopTimeline>();
  let previous = home;
  let etaMin = 0;
  let legsMin = 0;
  let outboundMin = 0;
  let lastDeparture = '';

  measurable.forEach(({ orderId, coord }, index) => {
    // La ida va con el coeficiente de carretera; los tramos entre paradas, con el
    // urbano. Es la única diferencia con la versión anterior, y es la que hacía que
    // un tramo de ciudad saliera en un minuto.
    const legMin = Math.round(haversineKm(previous, coord) * (index === 0 ? haul : urban));
    if (index === 0) outboundMin = legMin;
    else legsMin += legMin;
    // La atención de la parada ANTERIOR ya transcurrió cuando se sale hacia esta.
    etaMin += legMin + (index === 0 ? 0 : serviceMinPerStop);
    const arrivalTime = addMinutesToClock(shiftStart, etaMin);
    const departureTime = addMinutesToClock(arrivalTime, serviceMinPerStop);
    lastDeparture = departureTime || lastDeparture;
    stops.set(orderId, {
      arrivalTime,
      departureTime,
      serviceMin: serviceMinPerStop,
      // `0` en la primera, como el contrato: su tramo vive en `etaMin`.
      legDurationMin: index === 0 ? 0 : legMin,
      etaMin,
      // La estimación no modela viajes: todo es el primero, y sin recargas el
      // divisor no se pinta.
      trip: 1,
      estimated: true,
    });
    previous = coord;
  });

  // El regreso comparte régimen con la ida: es el mismo trayecto al revés.
  const returnMin = Math.round(haversineKm(previous, home) * haul);
  const travelMin = outboundMin + legsMin + returnMin;
  const serviceMin = measurable.length * (serviceMinPerStop ?? 0);

  return {
    timeline: {
      estimated: true,
      outboundMin,
      departureTime: outboundMin > 0 ? addMinutesToClock(shiftStart, 0) : '',
      returnMin,
      returnArrivalTime: returnMin > 0 ? addMinutesToClock(lastDeparture, returnMin) : '',
      reloads: [],
      stops,
    },
    // El total sale de los MISMOS tramos redondeados que la columna.
    day: { travelMin, serviceMin, totalMin: travelMin + serviceMin },
  };
}

/**
 * Minutos por encima de lo que **dura** el turno.
 *
 * Contra la duración y no contra una hora de reloj: la estimación es una jornada,
 * no un horario. No sabe a qué hora llega a ningún lado —para eso está el
 * recálculo— así que compararla con un reloj sería darle una precisión que no
 * tiene.
 */
export function estimateExceedsShiftMin(totalMin: number, shift: Shift | undefined): number {
  const start = clockToMinutes(shift?.start);
  const end = clockToMinutes(shift?.end);
  if (start === null || end === null) return 0;
  const shiftMin = end - start;
  if (!(shiftMin > 0)) return 0;
  return Math.max(0, Math.round(totalMin) - shiftMin);
}
